import type { Styles } from '@adornis/ass/style.js';
import { ChemistryRootElement } from '@adornis/chemistry/chemistry-root-element.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-button.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import { LASUser } from '@adornis/digitale-helden-shared/db/las-user.js';
import { Permissions } from '@adornis/digitale-helden-shared/db/permissions.js';
import '@adornis/digitale-helden-shared/tracking/client/consent-tool.js';
import { consentGroups } from '@adornis/digitale-helden-shared/tracking/client/variables/consentGroups.js';
import { initApp } from '@adornis/functional-lit/init-app.js';
import { makeRouter } from '@adornis/router/client/make-router.js';
import { authenticationGuard } from '@adornis/user-forms/authentication-guard.js';
import { permissionGuard } from '@adornis/user-forms/permission-guard.js';
import '@adornis/user-forms/x-login.js';
import { _subscribeCurrentUser, currentUser, updateCurrentUser } from '@adornis/users/client/currentUser.js';
import { watch } from '@lit-labs/preact-signals';
import { provide } from '@lit/context';
import { html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { MarketingFooterLinkExtension } from '../buildify/client/link-extensions/MarketingFooterLinkExtension.js';
import { MarketingTopbarLinkExtension } from '../buildify/client/link-extensions/MarketingTopbarLinkExtension.js';
import { MarketingProductTeaserCard } from '../buildify/client/MarketingProductTeaserCardExtension.js';
import { MarketingTopbarExtension } from '../buildify/client/MarketingTopbarExtension.js';
import { GlobalComponent } from '../db/GlobalComponent.js';
import { loginMarketingUser } from '../features/authentication/api/loginMarketingUser.js';
import './admin/marketing-page-admin.js';
import { adminRouter } from './admin/marketing-page-admin.js';
import './components/marketing-fallback.js';
import './components/marketing-global-component.js';
import { currentUserContext } from './contexts.js';

export const mainRouter = makeRouter(
  {
    admin: {
      path: '/admin*',
      render: params => {
        return authenticationGuard(
          () =>
            html`
              ${permissionGuard(
                Permissions.TeamPermission.VIEW,
                () => html` <marketing-page-admin></marketing-page-admin> `,
                { forbiddenTemplate: html` insufficient permissions ` },
              )}
            `,
          html`
            <x-login
              .login=${({ username, password }) => {
                loginMarketingUser(username, password)().then(async userID => {
                  updateCurrentUser(void 0, false);
                  await _subscribeCurrentUser({ userID, userClass: LASUser._class });
                });
              }}
            ></x-login>
          `,
          html` loading `,
        );
      },
      subRouter: adminRouter,
    },
  },
  {
    fallback: params => {
      return html`
        <x-flex h100 ${css({ overflowY: 'auto', overflowX: 'hidden' })}>
          <!-- nav -->
          <marketing-global-component
            ${css({
              position: 'sticky',
              top: '0',
              background: '#fff',
              zIndex: '99999',
            })}
            .identifier=${GlobalComponent.identifier.GLOBAL_NAV}
            .additionalExtensions=${[
              MarketingTopbarExtension,
              MarketingProductTeaserCard,
              MarketingTopbarLinkExtension,
            ]}
          ></marketing-global-component>
          <marketing-fallback></marketing-fallback>
          <marketing-global-component
            .identifier=${GlobalComponent.identifier.GLOBAL_FOOTER}
            .additionalExtensions=${[MarketingFooterLinkExtension]}
          ></marketing-global-component>
        </x-flex>
      `;
    },
  },
);

mainRouter.init();

initApp(() => html` <main-element></main-element> `);

@customElement('main-element')
export class MainElement extends ChemistryRootElement {
  @provide({ context: currentUserContext })
  @state()
  private readonly _currentUser = currentUser<LASUser>();

  override render() {
    return html`
      ${watch(mainRouter.outlet)}
      <consent-tool .groups=${consentGroups}></consent-tool>
    `;
  }

  override styles() {
    return [
      ...super.styles(),
      { ':host': { overflowX: 'hidden', overflowY: 'auto', color: this.colors.tone.black } },
    ] as Styles[];
  }
}
